// Types + fetch for the admin Failure config screen's baseline-load control
// (#306, ADR 0065 — mirrors failureConfig.ts's ADR 0027 shape). Same
// `/baseline-load` contract, behind the BFF at `/api/admin/baseline-load`
// with cookie auth.

export const BASELINE_LOAD_RATES = ["rate(5 minutes)", "rate(15 minutes)", "rate(30 minutes)", "rate(60 minutes)"] as const;
export type BaselineLoadRate = (typeof BASELINE_LOAD_RATES)[number];

export interface BaselineLoadConfig {
  enabled: boolean;
  rate: BaselineLoadRate;
}

/** The Terraform seed default (`var.enable_baseline_load` / `var.baseline_load_rate`). */
export const DEFAULT_BASELINE_LOAD_CONFIG: BaselineLoadConfig = {
  enabled: true,
  rate: "rate(15 minutes)",
};

export type BaselineLoadResponse =
  | { kind: "config"; body: BaselineLoadConfig }
  | { kind: "unauthorized" }
  | { kind: "unavailable"; status: number; error: string };

export async function fetchBaselineLoad(): Promise<BaselineLoadResponse> {
  let res: Response;
  try {
    res = await fetch("/api/admin/baseline-load", { credentials: "include" });
  } catch (err) {
    return { kind: "unavailable", status: 0, error: err instanceof Error ? err.message : "request failed (network)" };
  }
  if (res.status === 401) {
    return { kind: "unauthorized" };
  }
  if (res.status === 200) {
    return { kind: "config", body: (await res.json()) as BaselineLoadConfig };
  }
  return { kind: "unavailable", status: res.status, error: await errorMessage(res) };
}

export type PutBaselineLoadResponse =
  | { kind: "ok"; body: BaselineLoadConfig }
  | { kind: "unauthorized" }
  | { kind: "error"; status: number; error: string };

export async function putBaselineLoad(config: BaselineLoadConfig): Promise<PutBaselineLoadResponse> {
  let res: Response;
  try {
    res = await fetch("/api/admin/baseline-load", {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(config),
    });
  } catch (err) {
    return { kind: "error", status: 0, error: err instanceof Error ? err.message : "request failed (network)" };
  }
  if (res.status === 401) {
    return { kind: "unauthorized" };
  }
  if (res.status === 200) {
    return { kind: "ok", body: (await res.json()) as BaselineLoadConfig };
  }
  return { kind: "error", status: res.status, error: await errorMessage(res) };
}

async function errorMessage(res: Response): Promise<string> {
  let error = `${res.status} ${res.statusText}`.trim();
  try {
    const b = (await res.json()) as { error?: string };
    if (b?.error) error = b.error;
  } catch {
    /* keep the status line */
  }
  return error;
}
